import { useState, useEffect } from 'react';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import { Share2, Twitter, Linkedin, MessageCircle, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function CommunityFeed() {
  const [wins, setWins] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadWins();
  }, []);

  const loadWins = async () => {
    try {
      const response = await API.get('/community/wins');
      setWins(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load community wins');
      setLoading(false);
    }
  };

  const shareToTwitter = (win) => {
    const text = `Just saved £${win.amount_saved} using negotiation! 💰 Master your negotiations with Negotiation Ninja AI. #NegotiationNinja #MoneyTips`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(window.location.origin)}`;
    window.open(url, '_blank');
  };

  const shareToLinkedIn = (win) => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}`;
    window.open(url, '_blank');
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.origin);
    toast.success('Link copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navigation />
      
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <MessageCircle className="text-green-500 w-16 h-16 mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-4xl font-bold mb-3" data-testid="community-title">Community Wins</h1>
          <p className="text-gray-400">Real success stories from our ninja community</p>
        </div>

        {wins.length === 0 ? (
          <div className="ninja-card text-center py-12">
            <TrendingUp className="text-gray-600 w-16 h-16 mx-auto mb-4" />
            <p className="text-gray-400">No public wins yet. Share yours to inspire others!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {wins.map((win, idx) => (
              <div key={idx} className="ninja-card" data-testid={`win-card-${idx}`}>
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="font-['Outfit'] text-xl font-bold mb-2">{win.title}</h3>
                    <p className="text-gray-400 mb-3">{win.description}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="px-3 py-1 bg-green-500/20 border border-green-500/40 rounded-full text-green-400">
                        {win.category}
                      </span>
                      <span className="text-gray-500">
                        {new Date(win.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <div className="text-3xl font-bold text-green-500">£{win.amount_saved.toFixed(0)}</div>
                    <div className="text-sm text-gray-400">saved</div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4 border-t border-white/10">
                  <Button
                    onClick={() => shareToTwitter(win)}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-white/20 hover:bg-white/10"
                    data-testid={`share-twitter-${idx}`}
                  >
                    <Twitter className="w-4 h-4 mr-2" /> Twitter
                  </Button>
                  <Button
                    onClick={() => shareToLinkedIn(win)}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-white/20 hover:bg-white/10"
                    data-testid={`share-linkedin-${idx}`}
                  >
                    <Linkedin className="w-4 h-4 mr-2" /> LinkedIn
                  </Button>
                  <Button
                    onClick={copyLink}
                    variant="outline"
                    size="sm"
                    className="border-white/20 hover:bg-white/10"
                    data-testid={`copy-link-${idx}`}
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default CommunityFeed;